import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import {
  FolderOpen,
  Trash2,
  Scale,
  RefreshCw,
  Check,
  Mail,
  Building2,
  User,
  Download,
  FileDown,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LibraryView({ passcode, onOpen, onCompare, onPdf }) {
  const { t, locale } = useI18n();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);

  const headers = { "X-Admin-Passcode": passcode };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, { headers });
      setItems(data);
    } catch (e) {
      console.error(e);
      toast.error(t("lib.loadFailed"));
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers });
      setItems((prev) => prev.filter((s) => s.id !== id));
      setSelected((prev) => prev.filter((x) => x !== id));
      toast.success(t("lib.deleted"));
    } catch (e) {
      toast.error(t("lib.deleteFailed"));
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const toggleConvert = async (lead) => {
    try {
      const { data } = await axios.patch(
        `${API}/leads/${lead.id}/convert`,
        { converted: !lead.converted },
        { headers }
      );
      setItems((prev) => prev.map((s) => (s.id === lead.id ? data : s)));
      toast.success(data.converted ? t("lib.markedConverted") : t("lib.markedNot"));
    } catch (e) {
      toast.error(t("lib.updateFailed"));
    }
  };

  const selectedItems = items?.filter((s) => selected.includes(s.id)) || [];

  const exportCsv = () => {
    if (!items || items.length === 0) return;
    const cols = [
      "Name", "Email", "Company", "Role", "Company Size", "Captured Date",
      "Org Readiness", "Top Gaps", "Model", "Language",
    ];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = items.map((r) => {
      const recs = r.recommendations || {};
      return [
        r.name, r.email, r.company, r.role, r.company_size,
        r.created_at ? new Date(r.created_at).toISOString().slice(0, 10) : "",
        recs.org_readiness_score ?? "",
        (recs.top_gaps || []).join("; "),
        r.model_type,
        (r.lang || "en").toUpperCase(),
      ].map(esc).join(",");
    });
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `coachscore-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("lib.exported", { n: items.length }));
  };

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500" data-testid="library-loading">
        <RefreshCw size={20} className="animate-spin mr-2" /> {t("lib.loading")}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="library-empty">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-500/10 mb-4">
          <FolderOpen size={28} className="text-indigo-400" />
        </span>
        <h3 className="font-heading text-xl font-bold text-slate-100 mb-2">{t("lib.emptyTitle")}</h3>
        <p className="text-sm text-slate-500 max-w-md">{t("lib.emptyDesc")}</p>
      </div>
    );
  }

  return (
    <div data-testid="library-view">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-50">{t("lib.title")}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{t("lib.count", { n: items.length })}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-9 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
            onClick={exportCsv}
            data-testid="export-csv-btn"
          >
            <Download size={14} className="mr-1.5" /> {t("lib.export")}
          </Button>
          <Button
            variant="outline"
            className="h-9 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
            onClick={load}
            data-testid="library-refresh-btn"
          >
            <RefreshCw size={14} className="mr-1.5" /> {t("lib.refresh")}
          </Button>
          <Button
            disabled={selectedItems.length !== 2}
            onClick={() => onCompare(selectedItems)}
            className="h-9 bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40"
            data-testid="compare-selected-btn"
          >
            <Scale size={14} className="mr-1.5" /> {t("lib.compare", { n: selectedItems.length })}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((r, i) => {
          const isSelected = selected.includes(r.id);
          return (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
            >
              <Card
                data-testid={`library-card-${r.id}`}
                className={`bg-[#111827] border p-5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-500/50 ${
                  isSelected ? "border-indigo-500 ring-1 ring-indigo-500/50" : "border-slate-800"
                }`}
                onClick={() => toggleSelect(r.id)}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <h3 className="font-heading text-base md:text-lg font-bold text-slate-50 leading-snug truncate flex items-center gap-1.5">
                      <Building2 size={15} className="text-indigo-400 shrink-0" />
                      {r.company}
                    </h3>
                    <p className="text-xs text-slate-400 truncate flex items-center gap-1.5 mt-1">
                      <User size={12} className="shrink-0" /> {r.name}
                      {r.role ? ` · ${r.role}` : ""}
                    </p>
                    <a
                      href={`mailto:${r.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-indigo-300 hover:text-indigo-200 truncate flex items-center gap-1.5 mt-0.5"
                      data-testid={`library-email-${r.id}`}
                    >
                      <Mail size={12} className="shrink-0" /> {r.email}
                    </a>
                  </div>
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                      isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-600 text-transparent"
                    }`}
                    data-testid={`library-select-${r.id}`}
                  >
                    <Check size={13} />
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                    {t(`model.${r.model_type}`)}
                  </Badge>
                  <Badge className="bg-slate-800 text-slate-300 border-slate-700 uppercase">
                    {(r.lang || "en")}
                  </Badge>
                  {r.company_size && (
                    <Badge className="bg-slate-800 text-slate-300 border-slate-700">{r.company_size}</Badge>
                  )}
                  {r.recommendations ? (
                    <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30">{t("lib.aiPlan")}</Badge>
                  ) : (
                    <Badge className="bg-slate-800 text-slate-400 border-slate-700">{t("lib.noAiPlan")}</Badge>
                  )}
                  {r.converted && (
                    <Badge
                      className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                      data-testid={`library-converted-${r.id}`}
                    >
                      {t("lib.converted")}
                    </Badge>
                  )}
                </div>

                <p className="text-[11px] text-slate-500 mb-3">{t("lib.captured", { date: fmtDate(r.created_at) })}</p>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <MiniStat
                    label={t("lib.readiness")}
                    value={
                      r.recommendations?.org_readiness_score != null
                        ? `${r.recommendations.org_readiness_score}%`
                        : "—"
                    }
                  />
                  <MiniStat label={t("lib.programs")} value={r.recommendations?.programs?.length ?? "—"} />
                </div>

                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    className="flex-1 h-8 bg-indigo-600 hover:bg-indigo-500 text-white"
                    onClick={() => onOpen(r)}
                    data-testid={`library-open-${r.id}`}
                  >
                    <FolderOpen size={13} className="mr-1.5" /> {t("lib.viewReport")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`h-8 bg-transparent ${
                      r.converted
                        ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        : "border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`}
                    onClick={() => toggleConvert(r)}
                    data-testid={`library-convert-${r.id}`}
                    title={r.converted ? t("lib.markNotConverted") : t("lib.markConverted")}
                  >
                    <CheckCircle2 size={13} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                    onClick={() => onPdf?.(r)}
                    data-testid={`library-pdf-${r.id}`}
                    title={t("admin.pdfTooltip")}
                  >
                    <FileDown size={13} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 bg-transparent border-slate-700 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/40"
                    onClick={() => remove(r.id)}
                    data-testid={`library-delete-${r.id}`}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-900/50 border border-slate-800 p-2.5 text-center">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-100 tabular-nums">{value}</p>
    </div>
  );
}
